import React, { useState, useEffect, useCallback } from 'react';
import classnames from 'classnames';
import { FileOutlined, DeleteFilled, EasyTwinUploadColored } from '@easyv/react-icons';
import { message, Upload } from '@easyv/react-components';
import {
  Component,
  FileApi,
  ResourceManager,
  uploadMulti,
  FeatModelExt,
  FileTypeEnum,
  FileUtil,
} from '@easytwin/runtime';
import {
  resolveAbsoluteUrl,
  getAssetsPrefix,
  getFileNameAndExt,
  isNil,
  parseFileSize,
  isFileAccepted,
  sliceFileName,
} from '@/utils';
import { ProButton } from '../ProButton';
import { MAX_UPLOAD_FILE_SIZE, MAX_UPLOAD_IMAGE_SIZE, MAX_UPLOAD_MODEL_SIZE } from './utils';
import { HdrPreviewer } from './HdrPreviewer';
import { useReplaceModelInfoStore } from '@/store/ImportModelInfoStore';
import { ReplaceMaterialModal } from './ReplaceMaterialModal';
import { ImportModelInfoModal } from './ImportModelInfoModal';
import { ProModal } from './ProModal';
import { AssetListPopupForQuickSelect } from '../AssetListPopupForQuickSelect';

export interface FileUploadProps {
  maxFileSize?: number;
  maxImageSize?: number;
  maxModelSize?: number;
  value?: string | null;
  className?: string;
  innerContextClassName?: string;
  /** 用户选择文件时默认选择范围，由于可以强行使用所有范围选择其他格式文件，因此需要uploadAcceptExt做校验 */
  uploadAccept?: string;
  uploadAcceptExt?: string[];
  hoverContent?: React.ReactNode;
  hoverContentExtra?: React.ReactNode;
  backgroundSize?: string;
  showDeleteBtn?: boolean;
  disabled?: boolean;
  onCompleted?: (url: string) => void;

  // 是否使用资产库上传
  isAssetUpload?: boolean;

  // model专用
  // uploadAcceptExt包含glb格式时必传，用于模型校验
  comp?: Component;
}

export const FileUpload: React.FC<FileUploadProps> = ({
  maxFileSize = MAX_UPLOAD_FILE_SIZE,
  maxImageSize = MAX_UPLOAD_IMAGE_SIZE,
  maxModelSize = MAX_UPLOAD_MODEL_SIZE,
  value = '',
  className = '',
  innerContextClassName = '',
  uploadAcceptExt = [],
  uploadAccept = uploadAcceptExt.map((a) => '.' + a).join(','),
  hoverContent,
  hoverContentExtra,
  backgroundSize = 'contain',
  showDeleteBtn = true,
  disabled,
  isAssetUpload = false,
  comp,
  onCompleted,
}) => {
  const [url, setUrl] = useState<string>('');
  const { info, addInfo, clearInfo } = useReplaceModelInfoStore();
  const [isImportModelInfoModalOpen, setIsImportModelInfoModalOpen] = useState(false);
  const warnFile = React.useRef<File>();
  const [isReplaceMatModalOpen, setIsReplaceMatModalOpen] = useState(false);
  const [uploadingUrl, setUploadingUrl] = useState<string>();
  const [isImporting, setIsImporting] = useState(false);
  const [isAssetListOpen, setIsAssetListOpen] = useState<boolean>(false);

  useEffect(() => {
    if (typeof value === 'string') {
      setUrl(value || '');
    }
  }, [value]);

  const checkExt = useCallback(
    (name: string) => {
      if (uploadAcceptExt.length !== 0) {
        for (const i of uploadAcceptExt) {
          if (name.endsWith(i)) {
            return true;
          }
        }
        return false;
      }
      return true;
    },
    [uploadAcceptExt],
  );

  const uploadAndReplaceFile = useCallback(async (file: File) => {
    const url = await FileApi.uploadMulti({
      file,
      fileName: sliceFileName(file.name),
      type: 'model',
    });

    setUploadingUrl(url);
    setIsReplaceMatModalOpen(true);
  }, []);

  const verifyAndReplaceModel = useCallback(
    async (file: File, onVerified: () => void) => {
      console.log('replace model');
      const ext = file.name.slice(file.name.lastIndexOf('.') + 1).toLocaleLowerCase();
      if (!FeatModelExt.includes(ext)) {
        message.error(`格式不正确，仅支持${FeatModelExt.join('/')}`);
        return;
      }
      if (file.size >= MAX_UPLOAD_MODEL_SIZE) {
        message.error(`模型大小超出${MAX_UPLOAD_MODEL_SIZE / 1024 / 1024}MB限制`);
        return;
      }
      const info = await comp?.engine.getManager(ResourceManager).getModelInfo(file);
      if (isNil(info)) {
        message.error('发生未知错误');
        return;
      }
      if (info.errors.length > 0) {
        addInfo('error', file.name, info.errors, file);
        setIsImportModelInfoModalOpen(true);
        return;
      }
      if (info.warnings.length > 0) {
        addInfo('warn', file.name, info.warnings, file);
        warnFile.current = file;
        setIsImportModelInfoModalOpen(true);
        return;
      }
      onVerified();
      return false;
    },
    [addInfo],
  );

  const validateFile = useCallback(
    (file: File) => {
      // check ext
      const ok = checkExt(file.name);
      if (!ok) {
        throw new Error(`文件格式不支持，允许的格式为${uploadAcceptExt.join(',')}`);
      }
      const { ext } = getFileNameAndExt(file);
      const mode = FileUtil.calcFileType(ext);
      // check size
      if (mode === FileTypeEnum.IMAGE && file.size > maxImageSize) {
        throw new Error(`图片大小不得超过${parseFileSize(maxImageSize)}`);
      } else if (
        (mode === FileTypeEnum.VIDEO || mode === FileTypeEnum.HDR) &&
        file.size > maxFileSize
      ) {
        throw new Error(`文件大小不得超过${parseFileSize(maxFileSize)}`);
      } else if (mode === FileTypeEnum.MODEL && file.size > maxModelSize) {
        throw new Error(`模型大小不得超过${parseFileSize(maxModelSize)}`);
      }

      // check model
      if (mode === FileTypeEnum.MODEL) {
        if (isFileAccepted(file, '.glb')) {
          verifyAndReplaceModel(file, () => {
            uploadAndReplaceFile(file);
          });
          return false;
        }
      }
      return true;
    },
    [checkExt, maxFileSize, maxImageSize, maxModelSize, uploadAcceptExt],
  );

  const handleUploadFile = React.useCallback(
    async (file: File) => {
      try {
        setIsImporting(true);
        validateFile(file);

        const { ext } = getFileNameAndExt(file);
        const mode = FileUtil.calcFileType(ext);
        if (isNil(mode)) {
          throw new Error(`${ext}格式未受支持`);
        }
        const res = await uploadMulti({
          file,
          fileName: file.name,
          type: mode,
        });
        setUrl(res);
        onCompleted?.(res);

        return false;
      } catch (error) {
        message.error((error as Error).message);
        return false;
      } finally {
        setIsImporting(false);
      }
    },
    [validateFile, onCompleted],
  );

  const handleRemove = () => {
    setUrl('');
    onCompleted?.('');
  };

  const innerRender = (url: string) => {
    const { ext } = getFileNameAndExt(url);
    const mode = FileUtil.calcFileType(ext);

    switch (mode) {
      case FileTypeEnum.IMAGE:
        return (
          <div className={classnames('w-full h-full', innerContextClassName)}>
            <div
              className='w-full h-full rounded'
              style={{
                objectFit: 'cover',
                backgroundImage: `url("${resolveAbsoluteUrl(getAssetsPrefix(), url)}")`,
                backgroundSize,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
              }}
            />
          </div>
        );
      case FileTypeEnum.HDR:
        return (
          <div className={classnames('w-full h-full', innerContextClassName)}>
            <HdrPreviewer
              className='w-full h-full rounded overflow-hidden'
              url={resolveAbsoluteUrl(getAssetsPrefix(), url)}
            />
          </div>
        );

      default:
        return (
          <div
            className={classnames(
              'h-full w-full flex flex-col gap-y-2 justify-center items-center',
              innerContextClassName,
            )}
          >
            <FileOutlined className='text-22 anticon' />
            <div className='w-full overflow-hidden flex justify-center items-center h-6'>
              <div className='text-xs text-white px-4 truncate'>
                {url.substring(url.lastIndexOf('/') + 1)}
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div
      className={classnames(
        'w-full h-[108px] bg-FFFFFF0A border border-borderColorBase rounded group relative',
        className,
      )}
    >
      {url ? (
        innerRender(url)
      ) : (
        <EasyTwinUploadColored
          className='w-full h-full flex items-center justify-center'
          size={64}
        />
      )}
      <div className='w-full h-full rounded absolute top-0 hidden group-hover:block bg-[rgba(0,0,0,0.5)]'>
        {hoverContent || (
          <>
            {/*  */}
            <div className='w-full h-full border border-primary rounded flex flex-col justify-center items-center gap-y-2'>
              {/* 资产库上传 */}
              {isAssetUpload && (
                <ProButton
                  size='small'
                  disabled={!isAssetUpload}
                  type='primary'
                  className='!w-20 block'
                  onClick={() => {
                    setIsAssetListOpen(true);
                  }}
                >
                  {url ? '资产库更新' : '资产库上传'}
                </ProButton>
              )}
              {/*  */}
              <Upload
                disabled={disabled || isImporting}
                accept={uploadAccept}
                showUploadList={false}
                beforeUpload={handleUploadFile}
              >
                <ProButton size='small' type='primary' className='!w-20 block'>
                  {url ? '本地文件更新' : '本地文件上传'}
                </ProButton>
              </Upload>

              {/*  */}
              {hoverContentExtra}
            </div>
            {/*  */}
            <div className='absolute top-0 right-0 p-2'>
              {url && showDeleteBtn && (
                <DeleteFilled className='anticon p-1 cursor-pointer' onClick={handleRemove} />
              )}
            </div>
          </>
        )}
      </div>
      <ImportModelInfoModal
        fileCount={1}
        data={info}
        open={isImportModelInfoModalOpen}
        onConfirmUpload={async () => {
          setIsImportModelInfoModalOpen(false);
          clearInfo();
          const file = warnFile.current;
          if (isNil(file)) return;
          await uploadAndReplaceFile(file);
          warnFile.current = undefined;
        }}
        onClose={() => {
          setIsImportModelInfoModalOpen(false);
          clearInfo();
        }}
      />
      {isReplaceMatModalOpen && (
        <ReplaceMaterialModal
          url={uploadingUrl}
          comp={comp}
          open={isReplaceMatModalOpen}
          onStart={() => {
            setIsImporting?.(true);
          }}
          onFinish={(success) => {
            if (success) {
              message.success('更新成功');
              console.log('更新成功');
            }
            setIsImporting?.(false);
          }}
          mode={'instanceModel'}
          onClose={() => {
            setIsReplaceMatModalOpen(false);
          }}
        />
      )}
      <ProModal
        alignCenter
        showFooter={false}
        style={{ width: '1072px' }}
        className='no-padding'
        title={<div className='text-white px-1'>资产库</div>}
        visible={isAssetListOpen}
        onCancel={() => {
          setIsAssetListOpen(false);
        }}
      >
        <AssetListPopupForQuickSelect
          height={500}
          selectedType='model'
          onSelect={(item: any) => {
            if (comp) {
              setUploadingUrl(item.url);
              setIsAssetListOpen(false);
              setIsReplaceMatModalOpen(true);
            }
          }}
        />
      </ProModal>
    </div>
  );
};
